import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRaceTrackStore } from '../useRaceTrackStore';
import type { RaceKeyframe } from '../types';

interface TrackTimelineProps {
  onSeek: (time: number) => void;
  onKeyframeSelect: (kf: RaceKeyframe) => void;
}

export function TrackTimeline({ onSeek, onKeyframeSelect }: TrackTimelineProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentTime = useRaceTrackStore((s) => s.editorTime);
  const keyframes = useRaceTrackStore((s) => s.keyframes);
  const duration = useRaceTrackStore((s) => s.duration);
  const selectedKeyframeId = useRaceTrackStore((s) => s.selectedKeyframeId);
  const updateKeyframeTime = useRaceTrackStore((s) => s.updateKeyframeTime);

  // 拖拽状态仅用于控制事件监听逻辑，不参与渲染，用 ref 避免额外重渲染
  const draggingPlayheadRef = useRef(false);
  const draggingKeyframeIdRef = useRef<string | null>(null);

  const durationClamped = duration > 0 ? duration : 8;
  const playheadPercent = Math.max(0, Math.min((currentTime / durationClamped) * 100, 100));
  const orderedKeyframes = keyframes.toSorted((a, b) => a.time - b.time);
  const firstPercent = orderedKeyframes[0] ? (orderedKeyframes[0].time / durationClamped) * 100 : 0;
  const lastPercent = orderedKeyframes.at(-1)
    ? (orderedKeyframes.at(-1)!.time / durationClamped) * 100
    : 0;

  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;

  const seekFromClientX = useCallback(
    (clientX: number): number => {
      if (!containerRef.current) return currentTimeRef.current;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min((clientX - rect.left) / rect.width, 1));
      return pct * durationClamped;
    },
    [durationClamped],
  );

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || draggingKeyframeIdRef.current) return;
    onSeek(seekFromClientX(e.clientX));
    draggingPlayheadRef.current = true;
  };

  // 用 ref 持有最新回调，避免 effect 随回调引用变化而重新订阅
  const onSeekRef = useRef(onSeek);
  onSeekRef.current = onSeek;
  const seekFromClientXRef = useRef(seekFromClientX);
  seekFromClientXRef.current = seekFromClientX;
  const updateKeyframeTimeRef = useRef(updateKeyframeTime);
  updateKeyframeTimeRef.current = updateKeyframeTime;

  // 始终挂载拖拽监听，通过 ref 判断是否处于拖拽状态
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingPlayheadRef.current && !draggingKeyframeIdRef.current) return;
      const targetTime = seekFromClientXRef.current(e.clientX);
      if (draggingPlayheadRef.current) {
        onSeekRef.current(targetTime);
        return;
      }
      if (draggingKeyframeIdRef.current) {
        updateKeyframeTimeRef.current(draggingKeyframeIdRef.current, targetTime);
        onSeekRef.current(targetTime);
      }
    };

    const handleMouseUp = () => {
      draggingPlayheadRef.current = false;
      draggingKeyframeIdRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const dotTicks = Array.from({ length: 80 }, (_, i) => ((i + 1) / 80) * 100);
  const secondTicks = Array.from({ length: Math.floor(durationClamped) + 1 }, (_, i) => i);

  return (
    <div className="relative mx-4 flex min-h-[56px] flex-1 flex-col select-none">
      {/* 刻度区域 */}
      <div className="relative mb-1 h-6">
        <div className="relative h-full w-full">
          {dotTicks.map((left) => (
            <div
              key={left}
              className="absolute bottom-0.5 size-0.5 -translate-x-1/2 rounded-full bg-white/20"
              style={{ left: `${left}%` }}
            />
          ))}
          {secondTicks.map((second) => {
            const left = (second / durationClamped) * 100;
            return (
              <div
                key={second}
                className="absolute bottom-0 flex h-full -translate-x-1/2 flex-col items-center justify-end select-none"
                style={{ left: `${left}%` }}
              >
                <span className="mb-0.5 text-[10px] text-white/50">{second}s</span>
                <div className="h-2 w-px bg-white/40" />
              </div>
            );
          })}
        </div>
      </div>

      {/* 轨道区域：关键帧垂直居中 */}
      <div className="flex flex-1 items-stretch">
        <div
          ref={containerRef}
          role="slider"
          tabIndex={0}
          aria-label={t('raceTrack.timelineTrack')}
          aria-valuemin={0}
          aria-valuemax={durationClamped}
          aria-valuenow={currentTime}
          className="relative min-h-10 flex-1 cursor-pointer rounded bg-white/[0.02]"
          onMouseDown={handleTimelineClick}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              onSeek(Math.max(0, currentTime - 0.1));
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              onSeek(Math.min(durationClamped, currentTime + 0.1));
            }
          }}
        >
          <div className="relative h-full w-full">
            {orderedKeyframes.length >= 1 && (
              <div
                className="absolute top-1/2 z-0 h-6 -translate-y-1/2 rounded-sm bg-emerald-500/50 hover:bg-emerald-500/70"
                style={{
                  left:
                    orderedKeyframes.length === 1
                      ? `calc(${firstPercent}% - 6px)`
                      : `${firstPercent}%`,
                  width:
                    orderedKeyframes.length === 1
                      ? '12px'
                      : `${Math.max(lastPercent - firstPercent, 0)}%`,
                }}
              />
            )}

            {orderedKeyframes.map((kf) => {
              const left = (kf.time / durationClamped) * 100;
              const markerLeft =
                kf.time <= 0 ? '6px' : kf.time >= durationClamped ? 'calc(100% - 6px)' : `${left}%`;
              const isSelected = kf.id === selectedKeyframeId;
              return (
                <button
                  key={kf.id}
                  type="button"
                  aria-label={t('raceTrack.keyframeAt', { time: kf.time.toFixed(2) })}
                  className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-grab border-none bg-transparent p-0 active:cursor-grabbing"
                  style={{ left: markerLeft }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onKeyframeSelect(kf);
                    draggingKeyframeIdRef.current = kf.id;
                  }}
                >
                  <div
                    className={`h-6 w-3 rounded-md transition-colors ${
                      isSelected
                        ? 'bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.75)] ring-2 ring-white/80'
                        : 'bg-emerald-400 hover:bg-emerald-200'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 指针：跨刻度区 + 间距 + 轨道区，其他内容不跨区域 */}
      <div
        className="pointer-events-none absolute inset-0 z-20 select-none"
        style={{ left: `${playheadPercent}%`, width: 0 }}
      >
        <button
          type="button"
          aria-label={t('raceTrack.playhead')}
          className="pointer-events-auto relative h-full cursor-grab border-none bg-transparent p-0 active:cursor-grabbing"
          onMouseDown={(e) => {
            e.stopPropagation();
            onSeek(seekFromClientX(e.clientX));
            draggingPlayheadRef.current = true;
          }}
        >
          {/* 数字标签 — 顶部，恢复背景色，可拖拽 */}
          <div className="pointer-events-auto absolute -top-0.5 -translate-x-1/2 rounded bg-indigo-500 px-1 py-0.5 text-[10px] text-white tabular-nums">
            {currentTime.toFixed(2)}
          </div>
          {/* 竖线 — 从数字下方延伸到底部 */}
          <div className="absolute top-5 left-0 h-[calc(100%-1.25rem)] w-0.5 bg-indigo-500" />
        </button>
      </div>
    </div>
  );
}
